import { Check, Lock } from 'lucide-react';
import React from 'react';
import {
  AGENT_CAPABILITIES,
  ALWAYS_ON_CAPABILITY_ID,
  type AgentCapability,
  type AgentCapabilityCategory,
} from '@/utils/agentCapabilities';

interface AgentCapabilityPickerProps {
  selected: Set<string>;
  onChange: (ids: Set<string>) => void;
}

const CATEGORY_ORDER: AgentCapabilityCategory[] = ['Core', 'Vision', 'Language', 'Image', 'Audio'];

/**
 * Toggle-card grid for the no-code Agent Builder — lets an owner choose which
 * functional capabilities their agent is allowed to use. Grouped by
 * category. Unavailable (blocked in this environment) capabilities render
 * disabled/greyed with a badge. textChat is always on and can't be toggled.
 */
export const AgentCapabilityPicker: React.FC<AgentCapabilityPickerProps> = ({ selected, onChange }) => {
  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    items: AGENT_CAPABILITIES.filter((c) => c.category === category),
  })).filter((g) => g.items.length > 0);

  const toggle = (capability: AgentCapability) => {
    if (!capability.available || capability.id === ALWAYS_ON_CAPABILITY_ID) return;
    const next = new Set(selected);
    if (next.has(capability.id)) next.delete(capability.id);
    else next.add(capability.id);
    onChange(next);
  };

  return (
    <div className="space-y-5">
      {grouped.map(({ category, items }) => (
        <div key={category}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2">{category}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {items.map((capability) => {
              const isAlwaysOn = capability.id === ALWAYS_ON_CAPABILITY_ID;
              const isChecked = isAlwaysOn || selected.has(capability.id);
              const disabled = !capability.available;

              return (
                <button
                  key={capability.id}
                  type="button"
                  onClick={() => toggle(capability)}
                  disabled={disabled || isAlwaysOn}
                  title={disabled ? 'Not available in this environment yet' : undefined}
                  className={`relative text-left rounded-2xl border p-3.5 transition-all duration-200 ${
                    disabled
                      ? 'opacity-50 cursor-not-allowed border-border bg-secondary/40'
                      : isAlwaysOn
                      ? 'border-primary/60 bg-accent/50 cursor-default'
                      : isChecked
                      ? 'border-primary bg-primary/10 shadow-sm cursor-pointer'
                      : 'border-border bg-card hover:border-primary/40 hover:bg-secondary/40 cursor-pointer'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-bold truncate">{capability.label}</p>
                        {isAlwaysOn && <Lock className="w-3 h-3 text-primary shrink-0" />}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{capability.description}</p>
                    </div>
                    <div
                      className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                        isChecked && !disabled
                          ? 'bg-[#00C805] border-[#00C805]'
                          : 'border-input bg-background'
                      }`}
                    >
                      {isChecked && !disabled && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                  </div>

                  {disabled && (
                    <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                      Not available in this environment yet
                    </span>
                  )}
                  {isAlwaysOn && (
                    <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
                      Always on
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AgentCapabilityPicker;
